import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { AuthService } from '@principle-theorem/ng-auth';
import {
  ContentContainerComponent,
  EmptyStateComponent,
} from '@principle-theorem/ng-principle-shared';
import { NgCommonModule, TypedFormControl } from '@principle-theorem/ng-shared';
import {
  extendSortingDataAccessor,
  ObservableDataSource,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import { Referral } from '@principle-theorem/principle-core';
import { IReferral } from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, WithRef } from '@principle-theorem/shared';
import { of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

@Component({
  selector: 'pr-referral-portal-dashboard',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatProgressBarModule,
    ContentContainerComponent,
    EmptyStateComponent,
  ],
  templateUrl: './referral-portal-dashboard.component.html',
  styleUrl: './referral-portal-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralPortalDashboardComponent {
  private _router = inject(Router);
  private _authService = inject(AuthService);

  sort = viewChild.required(MatSort);
  trackByRef = TrackByFunctions.ref<WithRef<IReferral>>();

  loading = signal(true);
  table: ObservableDataSource<WithRef<IReferral>>;
  searchControl = new TypedFormControl<string>('');

  readonly displayedColumns = [
    'patient',
    'practice',
    'referrer',
    'urgency',
    'uid',
  ];

  referrals$ = this._authService.authUser$.pipe(
    filterUndefined(),
    switchMap((user) => {
      if (!user.email) {
        return of([]);
      }
      return Referral.byContactAll$(user.email);
    })
  );

  constructor() {
    this.table = new ObservableDataSource(this.referrals$);

    this.table.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );

    this.table.filterPredicate = (
      data: WithRef<IReferral>,
      filter: string
    ): boolean => {
      const searchStr = filter.trim().toLowerCase();
      return (
        (data.uid?.toLowerCase().includes(searchStr) ?? false) ||
        (data.patient?.name.toLowerCase().includes(searchStr) ?? false) ||
        (data.practice?.name.toLowerCase().includes(searchStr) ?? false) ||
        (data.referrer?.name.toLowerCase().includes(searchStr) ?? false)
      );
    };

    this.searchControl.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed())
      .subscribe((value) => {
        this.table.filter = (value ?? '').trim().toLowerCase();
      });

    this.referrals$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.loading.set(false);
    });

    effect(() => {
      this.table.sort = this.sort();
    });
  }

  async viewReferral(referral: WithRef<IReferral>): Promise<void> {
    await this._router.navigate(['/referrals', referral.uid]);
  }

  private _sortingDataAccessor(
    data: WithRef<IReferral>,
    sortHeaderId: string
  ): string | undefined {
    switch (sortHeaderId) {
      case 'patient':
        return data.patient?.name.toLowerCase();
      case 'practice':
        return data.practice?.name.toLowerCase();
      case 'referrer':
        return data.referrer?.name.toLowerCase();
      case 'urgency':
        return data.urgency;
      case 'uid':
        return data.uid;
      default:
        return;
    }
  }
}
